import { ArrowDown, ArrowRight, ArrowUp, CalendarClock, FlaskConical, FolderGit2, Pencil, Plus, Route, ShieldAlert, Trash2, Users, X } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { onDuty, prodPatternProblem, shiftEndsAt } from '@orgus/protocol';
import type { ProjectOwnership, Risk, OwnershipRota, RotaOverride, RouteDecision, TeamsRoutingRule, TeamsState } from '@orgus/protocol';
import {
  Avatar,
  Badge,
  Button,
  DataTable,
  Drawer,
  EmptyState,
  IconButton,
  Input,
  Panel,
  Segmented,
  Select,
  Spinner,
  Tooltip,
  cn,
  formatAbsolute,
  toast,
  useNow,
} from '@orgus/ui';
import type { Column } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { useCan } from '../auth/useMe.ts';
import { CheckList } from './CheckList.tsx';
import { teamsClient } from './client.ts';
import { personOf, useMyLogin } from './model.ts';
import { AvatarStack } from './PresenceAvatars.tsx';
import { TeamsGate } from './TeamsGate.tsx';

const H = 3_600_000;

/** A login or @team as a short label. */
function targetLabel(data: TeamsState, t: string): string {
  if (t.startsWith('@')) return data.teams.find((x) => `@${x.handle}` === t)?.name ?? t;
  return personOf(data, t).name;
}

function OwnersCell({ data, owners }: { data: TeamsState; owners: string[] }) {
  if (!owners.length) return <span className="text-sm text-fg-subtle">No owners</span>;
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1.5">
      {owners.map((o) =>
        o.startsWith('@') ? (
          <Badge key={o} tone="accent" icon={<Users />}>
            {o}
          </Badge>
        ) : (
          <span key={o} className="inline-flex items-center gap-1.5 text-sm text-fg">
            <Avatar name={personOf(data, o).name} colorKey={o} size={18} />
            {personOf(data, o).name}
          </span>
        ),
      )}
    </span>
  );
}

function DutyCell({ data, rota, now, me }: { data: TeamsState; rota: OwnershipRota | null; now: number; me: string | null }) {
  if (!rota) return <span className="text-sm text-fg-subtle">No rota</span>;
  const who = onDuty(rota, now);
  if (!who) return <span className="text-sm text-fg-subtle">Nobody</span>;
  const cover = rota.overrides.find((o) => o.from <= now && now < o.to);
  const until = cover ? cover.to : shiftEndsAt(rota, now);
  return (
    <Tooltip content={cover ? `Covering${cover.note ? `: ${cover.note}` : ''}. Until ${formatAbsolute(until)}.` : `Shift ends ${formatAbsolute(until)}.`}>
      <span tabIndex={0} className="inline-flex items-center gap-1.5 text-sm text-fg">
        <Avatar name={personOf(data, who).name} colorKey={who} size={18} />
        {who === me ? 'You' : personOf(data, who).name}
        {cover && <Badge tone="queued">Covering</Badge>}
      </span>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Owners and rota
// ---------------------------------------------------------------------------

const SHIFT_OPTIONS = [
  { value: '8', label: '8 hours' },
  { value: '12', label: '12 hours' },
  { value: '24', label: '1 day' },
  { value: '168', label: '1 week' },
];

/** "Fri 23:48", short enough for a row. */
const shortTime = (ms: number) => new Date(ms).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

const toLocalInput = (ms: number) => {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const fromLocalInput = (s: string) => {
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : null;
};

function OwnershipDrawer({ data, projectId, projectName, onClose }: { data: TeamsState; projectId: string | null; projectName: string; onClose: () => void }) {
  const cur = data.ownership.find((o) => o.projectId === projectId);
  const [owners, setOwners] = useState<string[]>([]);
  const [members, setMembers] = useState<string[]>([]);
  const [shift, setShift] = useState('24');
  const [start, setStart] = useState('');
  const [overrides, setOverrides] = useState<RotaOverride[]>([]);
  const [cover, setCover] = useState<{ login: string; from: string; to: string; note: string }>({ login: '', from: '', to: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    setOwners(cur?.owners ?? []);
    setMembers(cur?.rota?.members ?? []);
    setShift(String(cur?.rota?.shiftHours ?? 24));
    const nine = new Date();
    nine.setHours(9, 0, 0, 0);
    setStart(toLocalInput(cur?.rota?.startsAt ?? nine.getTime()));
    setOverrides(cur?.rota?.overrides ?? []);
    setCover({ login: '', from: '', to: '', note: '' });
    setError(null);
    // Reset only when another project opens, not on every live update.
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const ownerOptions = useMemo(
    () => [
      ...data.teams.map((t) => ({ value: `@${t.handle}`, label: t.name, detail: `@${t.handle}`, leading: <Users className="size-3.5 text-accent" aria-hidden /> })),
      ...data.people.filter((p) => p.login !== 'owner').map((p) => ({ value: p.login, label: p.name, detail: `@${p.login}`, leading: <Avatar name={p.name} colorKey={p.login} size={20} /> })),
    ],
    [data.teams, data.people],
  );
  const peopleOptions = useMemo(() => data.people.filter((p) => p.login !== 'owner').map((p) => ({ value: p.login, label: p.name })), [data.people]);

  const move = (i: number, d: -1 | 1) => {
    const next = [...members];
    const j = i + d;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j]!, next[i]!];
    setMembers(next);
  };

  const addCover = () => {
    const from = fromLocalInput(cover.from);
    const to = fromLocalInput(cover.to);
    if (!cover.login || from === null || to === null) {
      setError('A cover shift needs a person, a start and an end.');
      return;
    }
    if (to <= from) {
      setError('A cover shift must end after it starts.');
      return;
    }
    setOverrides([...overrides, { login: cover.login, from, to, ...(cover.note.trim() ? { note: cover.note.trim() } : {}) }].sort((a, b) => a.from - b.from));
    setCover({ login: '', from: '', to: '', note: '' });
    setError(null);
  };

  const save = async () => {
    if (!projectId) return;
    const startsAt = fromLocalInput(start);
    if (members.length && startsAt === null) {
      setError('Pick when the first shift starts.');
      return;
    }
    setBusy(true);
    try {
      await teamsClient.setOwnership(projectId, {
        owners,
        rota: members.length ? { members, shiftHours: Number(shift), startsAt: startsAt!, overrides } : null,
      });
      toast.success(`${projectName} saved`, members.length ? `${members.length} ${members.length === 1 ? 'person' : 'people'} on the rota.` : undefined);
      onClose();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={!!projectId}
      onOpenChange={(v) => !v && onClose()}
      title={`Owners and rota for ${projectName}`}
      description="Owners get production requests. Whoever is on duty gets the rest, and is first in line when something escalates."
      width={560}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" className="ml-auto" loading={busy} onClick={() => void save()}>
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6 p-5">
        <CheckList label="Owners" options={ownerOptions} value={owners} onChange={setOwners} emptyText="Nobody has signed in yet." maxHeight="max-h-48" />

        <section className="flex flex-col gap-3" aria-labelledby="rota-h">
          <h3 id="rota-h" className="text-sm font-medium text-fg-muted">
            On-duty rota
          </h3>
          <CheckList label="Who takes turns" options={peopleOptions} value={members} onChange={setMembers} emptyText="Nobody has signed in yet." maxHeight="max-h-40" />
          {members.length > 1 && (
            <ol className="flex flex-col rounded-md border border-border" aria-label="Turn order">
              {members.map((m, i) => (
                <li key={m} className="flex items-center gap-2 border-b border-border-subtle px-3 py-1.5 text-sm last:border-b-0">
                  <span className="w-5 text-fg-subtle num">{i + 1}</span>
                  <Avatar name={personOf(data, m).name} colorKey={m} size={18} />
                  <span className="flex-1 truncate text-fg">{personOf(data, m).name}</span>
                  <IconButton label={`Move ${personOf(data, m).name} earlier`} icon={<ArrowUp />} size="xs" disabled={i === 0} onClick={() => move(i, -1)} />
                  <IconButton label={`Move ${personOf(data, m).name} later`} icon={<ArrowDown />} size="xs" disabled={i === members.length - 1} onClick={() => move(i, 1)} />
                </li>
              ))}
            </ol>
          )}
          {members.length > 0 && (
            <div className="flex flex-wrap items-end gap-3">
              <Select label="Shift length" value={shift} onValueChange={setShift} options={SHIFT_OPTIONS} wrapperClassName="w-36" />
              <Input type="datetime-local" label="First shift starts" value={start} onChange={(e) => setStart(e.target.value)} wrapperClassName="w-56" />
            </div>
          )}
        </section>

        {members.length > 0 && (
          <section className="flex flex-col gap-3" aria-labelledby="cover-h">
            <h3 id="cover-h" className="text-sm font-medium text-fg-muted">
              Cover shifts
            </h3>
            {overrides.length === 0 ? (
              <p className="text-sm text-fg-subtle">None. Add one when someone swaps or covers a shift; it wins over the rota while it lasts.</p>
            ) : (
              <ul className="flex flex-col rounded-md border border-border">
                {overrides.map((o, i) => (
                  <li key={`${o.login}-${o.from}`} className="flex items-center gap-2 border-b border-border-subtle px-3 py-1.5 text-sm last:border-b-0">
                    <Avatar name={personOf(data, o.login).name} colorKey={o.login} size={18} />
                    <span className="shrink-0 text-fg">{personOf(data, o.login).name}</span>
                    <Tooltip content={`${formatAbsolute(o.from)} to ${formatAbsolute(o.to)}${o.note ? `. ${o.note}` : ''}`}>
                      <span tabIndex={0} className="min-w-0 truncate text-fg-subtle">
                        {shortTime(o.from)} to {shortTime(o.to)}
                        {o.note ? `, ${o.note}` : ''}
                      </span>
                    </Tooltip>
                    <IconButton className="ml-auto" label="Remove this cover shift" icon={<X />} size="xs" onClick={() => setOverrides(overrides.filter((_, j) => j !== i))} />
                  </li>
                ))}
              </ul>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Select size="sm" label="Who covers" placeholder="Pick someone" value={cover.login} onValueChange={(v) => setCover({ ...cover, login: v })} options={peopleOptions} />
              <Input size="sm" label="Note" placeholder="Optional" value={cover.note} onChange={(e) => setCover({ ...cover, note: e.target.value })} maxLength={140} />
              <Input size="sm" type="datetime-local" label="From" value={cover.from} onChange={(e) => setCover({ ...cover, from: e.target.value })} />
              <Input size="sm" type="datetime-local" label="To" value={cover.to} onChange={(e) => setCover({ ...cover, to: e.target.value })} />
            </div>
            <Button size="sm" icon={<Plus />} className="self-start" onClick={addCover}>
              Add cover shift
            </Button>
          </section>
        )}
        {error && (
          <p role="alert" className="text-sm text-failed">
            {error}
          </p>
        )}
      </div>
    </Drawer>
  );
}

interface ProjectRow {
  id: string;
  name: string;
  reporting: boolean;
  own: ProjectOwnership | null;
}

function ProjectsPanel({ data, canManage }: { data: TeamsState; canManage: boolean }) {
  const projects = useOrgus((s) => s.full.projects);
  const me = useMyLogin();
  const now = useNow(60_000);
  const [params, setParams] = useSearchParams();
  const editing = params.get('project');
  const rows: ProjectRow[] = useMemo(() => {
    const out = new Map<string, ProjectRow>();
    for (const p of Object.values(projects)) out.set(p.id, { id: p.id, name: p.name, reporting: true, own: null });
    for (const o of data.ownership) out.set(o.projectId, { ...(out.get(o.projectId) ?? { id: o.projectId, name: o.projectId, reporting: false }), own: o });
    return [...out.values()];
  }, [projects, data.ownership]);
  const open = (id: string | null) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (id) n.set('project', id);
        else n.delete('project');
        return n;
      },
      { replace: true },
    );
  const columns: Column<ProjectRow>[] = [
    {
      key: 'name',
      header: 'Project',
      grow: true,
      text: (r) => r.name,
      sortValue: (r) => r.name.toLowerCase(),
      cell: (r) => (
        <span className="flex min-w-0 items-center gap-2.5">
          <FolderGit2 className="size-4 shrink-0 text-fg-subtle" aria-hidden />
          <span className="truncate font-medium text-fg">{r.name}</span>
          {!r.reporting && <Badge tone="offline">Not reporting</Badge>}
        </span>
      ),
    },
    { key: 'owners', header: 'Owners', cell: (r) => <OwnersCell data={data} owners={r.own?.owners ?? []} /> },
    { key: 'duty', header: 'On duty now', cell: (r) => <DutyCell data={data} rota={r.own?.rota ?? null} now={now} me={me} /> },
    {
      key: 'rota',
      header: 'Rota',
      hideBelow: 'lg',
      cell: (r) =>
        r.own?.rota ? (
          <span className="flex items-center gap-2 text-sm text-fg-muted">
            <AvatarStack people={r.own.rota.members.map((l) => personOf(data, l))} max={3} size={18} />
            {SHIFT_OPTIONS.find((o) => o.value === String(r.own!.rota!.shiftHours))?.label ?? `${r.own.rota.shiftHours} hours`} shifts
          </span>
        ) : (
          <span className="text-sm text-fg-subtle">None</span>
        ),
    },
  ];
  const editingRow = rows.find((r) => r.id === editing);
  return (
    <Panel title="Projects" sub={rows.length || undefined} icon={<FolderGit2 />} flush>
      <DataTable
        frameless
        label="Project owners and rota"
        rows={rows}
        rowKey={(r) => r.id}
        columns={columns}
        pageSize={false}
        searchable={rows.length > 8 ? { placeholder: 'Search projects' } : undefined}
        initialSort={{ key: 'name', dir: 'asc' }}
        activeKey={editing}
        empty={<EmptyState icon={<FolderGit2 />} title="No projects yet" description="Projects appear when a machine reports a checkout. Add one in Projects, then set its owners here." />}
        rowActions={(r) => (canManage ? <IconButton label={`Set owners and rota for ${r.name}`} icon={<Pencil />} size="sm" onClick={() => open(r.id)} /> : null)}
      />
      {!canManage && <p className="border-t border-border-subtle px-4 py-3 text-sm text-fg-subtle">Admins set owners and the rota.</p>}
      <OwnershipDrawer data={data} projectId={canManage && editingRow ? editingRow.id : null} projectName={editingRow?.name ?? ''} onClose={() => open(null)} />
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Routing rules
// ---------------------------------------------------------------------------

const RISKS: Risk[] = ['low', 'medium', 'high'];
const RISK_LABEL: Record<Risk, string> = { low: 'Low', medium: 'Medium', high: 'High' };

function conditionText(r: TeamsRoutingRule): string {
  const parts: string[] = [];
  if (r.when.prod === true) parts.push('Touches production');
  if (r.when.prod === false) parts.push('Does not touch production');
  if (r.when.risk?.length) parts.push(`Risk ${r.when.risk.map((x) => RISK_LABEL[x].toLowerCase()).join(' or ')}`);
  if (r.when.kinds?.length) parts.push(`Kind ${r.when.kinds.join(' or ')}`);
  if (r.when.projectIds?.length) parts.push(`In ${r.when.projectIds.length} ${r.when.projectIds.length === 1 ? 'project' : 'projects'}`);
  return parts.length ? parts.join(', ') : 'Any request';
}

function targetText(to: string, data: TeamsState): string {
  if (to === 'owners') return 'The project owners';
  if (to === 'on-duty') return 'Whoever is on duty';
  if (to === 'session-owner') return 'The session owner';
  return targetLabel(data, to);
}

function targetOptions(data: TeamsState) {
  return [
    { value: 'owners', label: 'The project owners' },
    { value: 'on-duty', label: 'Whoever is on duty' },
    { value: 'session-owner', label: 'The session owner' },
    ...data.teams.map((t) => ({ value: `@${t.handle}`, label: `${t.name} (@${t.handle})` })),
    ...data.people.filter((p) => p.login !== 'owner').map((p) => ({ value: p.login, label: `${p.name} (@${p.login})` })),
  ];
}

function RuleForm({ data, onAdd }: { data: TeamsState; onAdd: (r: TeamsRoutingRule) => void }) {
  const [name, setName] = useState('');
  const [prod, setProd] = useState<'any' | 'yes' | 'no'>('any');
  const [risk, setRisk] = useState<Risk[]>([]);
  const [to, setTo] = useState('owners');
  const [error, setError] = useState<string | null>(null);
  const add = () => {
    if (!name.trim()) {
      setError('Say what the rule does, for example High risk goes to Platform.');
      return;
    }
    onAdd({ id: `rte_new${Date.now().toString(36)}`, name: name.trim(), when: { ...(prod !== 'any' ? { prod: prod === 'yes' } : {}), ...(risk.length ? { risk } : {}) }, to, enabled: true });
    setName('');
    setRisk([]);
    setProd('any');
    setError(null);
  };
  return (
    <div className="flex flex-col gap-3 border-t border-border-subtle p-4">
      <p className="text-sm font-medium text-fg-muted">Add a rule</p>
      <Input size="sm" label="Name" placeholder="High risk goes to Platform" value={name} onChange={(e) => (setName(e.target.value), setError(null))} error={error ?? undefined} maxLength={100} />
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-fg-muted">Production</span>
          <Segmented
            label="Production"
            value={prod}
            onChange={setProd}
            options={[
              { value: 'any', label: 'Either' },
              { value: 'yes', label: 'Touches it' },
              { value: 'no', label: 'Does not' },
            ]}
          />
        </div>
        <fieldset className="flex flex-col gap-1.5">
          <legend className="mb-1.5 text-sm font-medium text-fg-muted">Risk</legend>
          <span className="flex items-center gap-3">
            {RISKS.map((r) => (
              <label key={r} className="flex cursor-pointer items-center gap-1.5 text-sm text-fg">
                <input type="checkbox" className="size-4 accent-accent" checked={risk.includes(r)} onChange={(e) => setRisk(e.target.checked ? [...risk, r] : risk.filter((x) => x !== r))} />
                {RISK_LABEL[r]}
              </label>
            ))}
          </span>
        </fieldset>
        <Select size="sm" label="Send to" value={to} onValueChange={setTo} options={targetOptions(data)} wrapperClassName="min-w-[220px] flex-1" />
        <Button size="sm" icon={<Plus />} onClick={add}>
          Add rule
        </Button>
      </div>
    </div>
  );
}

function RoutingPanel({ data, canManage }: { data: TeamsState; canManage: boolean }) {
  const [rules, setRules] = useState<TeamsRoutingRule[]>(data.routing.rules);
  const [busy, setBusy] = useState(false);
  const saved = JSON.stringify(data.routing.rules);
  const dirty = JSON.stringify(rules) !== saved;
  useEffect(() => {
    if (!dirty) setRules(data.routing.rules);
    // Live updates replace the list only while you have nothing unsaved.
  }, [saved]); // eslint-disable-line react-hooks/exhaustive-deps

  const move = (i: number, d: -1 | 1) => {
    const n = [...rules];
    const j = i + d;
    if (j < 0 || j >= n.length) return;
    [n[i], n[j]] = [n[j]!, n[i]!];
    setRules(n);
  };
  const save = async () => {
    setBusy(true);
    try {
      await teamsClient.setRouting({ rules });
      toast.success('Routing saved', 'New requests follow these rules now.');
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      title="Approval routing"
      sub={`${rules.filter((r) => r.enabled).length} of ${rules.length} on`}
      icon={<Route />}
      flush
      actions={
        canManage && dirty ? (
          <>
            <Button size="sm" variant="ghost" onClick={() => setRules(data.routing.rules)}>
              Discard
            </Button>
            <Button size="sm" variant="primary" loading={busy} onClick={() => void save()}>
              Save rules
            </Button>
          </>
        ) : undefined
      }
    >
      <p className="px-4 pt-3 text-sm text-fg-muted">Rules run top to bottom. The first one that matches and names somebody decides; one that names nobody (no owners, no rota) passes to the next.</p>
      <ol className="flex flex-col py-2" aria-label="Routing rules, in order">
        {rules.map((r, i) => (
          <li key={r.id} className={cn('flex items-center gap-3 px-4 py-2', !r.enabled && 'opacity-60')}>
            <span className="w-5 text-sm text-fg-subtle num">{i + 1}</span>
            {canManage ? (
              <input
                type="checkbox"
                aria-label={`${r.enabled ? 'Turn off' : 'Turn on'} ${r.name}`}
                className="size-4 accent-accent"
                checked={r.enabled}
                onChange={(e) => setRules(rules.map((x) => (x.id === r.id ? { ...x, enabled: e.target.checked } : x)))}
              />
            ) : (
              <Badge tone={r.enabled ? 'passed' : 'idle'} dot>
                {r.enabled ? 'On' : 'Off'}
              </Badge>
            )}
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-base text-fg">{r.name}</span>
              <span className="flex min-w-0 items-center gap-1.5 text-sm text-fg-muted">
                <span className="truncate">{conditionText(r)}</span>
                <ArrowRight className="size-3.5 shrink-0 text-fg-subtle" aria-label="goes to" />
                <span className="truncate text-fg">{targetText(r.to, data)}</span>
              </span>
            </span>
            {canManage && (
              <span className="flex items-center gap-0.5">
                <IconButton label={`Move ${r.name} up`} icon={<ArrowUp />} size="xs" disabled={i === 0} onClick={() => move(i, -1)} />
                <IconButton label={`Move ${r.name} down`} icon={<ArrowDown />} size="xs" disabled={i === rules.length - 1} onClick={() => move(i, 1)} />
                <IconButton label={`Delete ${r.name}`} icon={<Trash2 />} size="xs" onClick={() => setRules(rules.filter((x) => x.id !== r.id))} />
              </span>
            )}
          </li>
        ))}
        <li className="flex items-center gap-3 border-t border-dashed border-border-subtle px-4 pb-1 pt-3">
          <span className="w-5 text-sm text-fg-subtle">
            <ShieldAlert className="size-4" aria-hidden />
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="text-base text-fg">Otherwise</span>
            <span className="text-sm text-fg-muted">The mission owner, then the session owner. With neither, everyone sees it.</span>
          </span>
        </li>
      </ol>
      {canManage && rules.length < 20 && <RuleForm data={data} onAdd={(r) => setRules([...rules, r])} />}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Escalation and production patterns
// ---------------------------------------------------------------------------

const ESCALATE_OPTIONS = [
  { value: 'never', label: 'Never' },
  { value: '5', label: 'After 5 minutes' },
  { value: '10', label: 'After 10 minutes' },
  { value: '15', label: 'After 15 minutes' },
  { value: '30', label: 'After 30 minutes' },
  { value: '60', label: 'After 1 hour' },
];

function EscalationPanel({ data, canManage }: { data: TeamsState; canManage: boolean }) {
  const [pattern, setPattern] = useState('');
  const [error, setError] = useState<string | null>(null);
  const r = data.routing;
  const current = r.escalateAfterMinutes == null ? 'never' : String(r.escalateAfterMinutes);
  const options = ESCALATE_OPTIONS.some((o) => o.value === current) ? ESCALATE_OPTIONS : [...ESCALATE_OPTIONS, { value: current, label: `After ${current} minutes` }];
  const save = async (patch: Parameters<typeof teamsClient.setRouting>[0], done: string) => {
    try {
      await teamsClient.setRouting(patch);
      toast.success(done);
      return true;
    } catch (e) {
      toast.error(e);
      return false;
    }
  };
  const addPattern = async () => {
    const p = pattern.trim();
    if (!p) return;
    if (r.prodPatterns.includes(p)) {
      setError('That pattern is already on the list.');
      return;
    }
    const problem = prodPatternProblem(p);
    if (problem) {
      setError(`${problem} Simplify it, or drop the slashes to match the words as they are.`);
      return;
    }
    if (await save({ prodPatterns: [...r.prodPatterns, p] }, `${p} counts as production now`)) setPattern('');
  };
  return (
    <Panel title="Escalation and production" icon={<CalendarClock />}>
      <div className="flex flex-col gap-5">
        <Select
          label="Hand a request to the next person on call"
          hint="When nobody acknowledges a routed request in time, the next person on the project's rota joins it, then the owners."
          wrapperClassName="max-w-md"
          value={current}
          disabled={!canManage}
          onValueChange={(v) => void save({ escalateAfterMinutes: v === 'never' ? null : Number(v) }, v === 'never' ? 'Escalation is off' : 'Escalation saved')}
          options={options}
        />
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-fg-muted">A request touches production when its command, path or title contains one of these as whole words</span>
          <ul className="flex flex-wrap gap-1.5" aria-label="Production patterns">
            {r.prodPatterns.map((p) => (
              <li key={p}>
                <span className="inline-flex items-center gap-1 rounded-md border border-border bg-raised py-0.5 pl-2 pr-0.5 font-mono text-sm text-fg">
                  {p}
                  {canManage && <IconButton label={`Remove ${p}`} icon={<X />} size="xs" onClick={() => void save({ prodPatterns: r.prodPatterns.filter((x) => x !== p) }, `${p} removed`)} />}
                </span>
              </li>
            ))}
            {r.prodPatterns.length === 0 && <li className="text-sm text-fg-subtle">Nothing: no request counts as production.</li>}
          </ul>
          {canManage && (
            <form
              className="flex items-start gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                void addPattern();
              }}
            >
              <Input
                size="sm"
                aria-label="Add a production pattern"
                placeholder="Words such as prod-db, or /a regular expression/"
                value={pattern}
                onChange={(e) => (setPattern(e.target.value), setError(null))}
                error={error ?? undefined}
                wrapperClassName="max-w-md flex-1"
                spellCheck={false}
              />
              <Button size="sm" type="submit" icon={<Plus />} disabled={!pattern.trim()}>
                Add
              </Button>
            </form>
          )}
        </div>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Try it
// ---------------------------------------------------------------------------

function TryPanel({ data }: { data: TeamsState }) {
  const projects = useOrgus((s) => s.full.projects);
  const me = useMyLogin();
  const list = useMemo(() => Object.values(projects).sort((a, b) => a.name.localeCompare(b.name)), [projects]);
  const [projectId, setProjectId] = useState(list[0]?.id ?? '');
  const [subject, setSubject] = useState('wrangler deploy --env production');
  const [risk, setRisk] = useState<Risk>('medium');
  const [result, setResult] = useState<RouteDecision | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!projectId && list[0]) setProjectId(list[0].id);
  }, [list, projectId]);
  useEffect(() => {
    let live = true;
    setBusy(true);
    const t = setTimeout(() => {
      teamsClient
        .route({ projectId: projectId || null, subject, risk, kind: 'tool' })
        .then((r) => live && (setResult(r), setError(null)))
        .catch((e: unknown) => live && setError(e))
        .finally(() => live && setBusy(false));
    }, 250);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [projectId, subject, risk, data.rev]);
  return (
    <Panel title="Try a request" icon={<FlaskConical />} sub="Nothing is sent">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <Select label="Project" value={projectId} onValueChange={setProjectId} options={list.map((p) => ({ value: p.id, label: p.name }))} placeholder="No projects yet" wrapperClassName="w-48" />
          <Input label="Command or path" value={subject} onChange={(e) => setSubject(e.target.value)} wrapperClassName="min-w-[260px] flex-1" className="font-mono" spellCheck={false} />
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-fg-muted">Risk</span>
            <Segmented label="Risk" value={risk} onChange={setRisk} options={RISKS.map((r) => ({ value: r, label: RISK_LABEL[r] }))} />
          </div>
        </div>
        <div className="flex min-h-[52px] items-center gap-3 rounded-md border border-border bg-raised px-3 py-2" aria-live="polite">
          {error != null ? (
            <span className="text-sm text-failed">Could not preview routing. {error instanceof Error ? error.message : ''}</span>
          ) : !result ? (
            <Spinner />
          ) : result.assignees.length ? (
            <>
              <AvatarStack people={result.assignees.map((l) => personOf(data, l))} max={4} size={24} />
              <span className="flex min-w-0 flex-col">
                <span className="text-base text-fg">
                  Goes to {result.assignees.map((l) => (l === me ? 'you' : personOf(data, l).name)).join(', ')}
                  {result.prod && (
                    <Badge tone="waiting" className="ml-2">
                      Production
                    </Badge>
                  )}
                </span>
                <span className="truncate text-sm text-fg-muted">{result.why}</span>
              </span>
            </>
          ) : (
            <span className="flex flex-col">
              <span className="text-base text-fg">Stays with everyone</span>
              <span className="text-sm text-fg-muted">{result.why}</span>
            </span>
          )}
          {busy && result && <Spinner className="ml-auto" />}
        </div>
      </div>
    </Panel>
  );
}

/** Settings > Ownership: project owners and rota, approval routing, escalation, and a preview. */
export function OwnershipView() {
  const canManage = useCan('admin');
  return (
    <TeamsGate label="ownership">
      {(data) => (
        <div className="flex flex-col gap-4">
          <ProjectsPanel data={data} canManage={canManage} />
          <RoutingPanel data={data} canManage={canManage} />
          <EscalationPanel data={data} canManage={canManage} />
          <TryPanel data={data} />
        </div>
      )}
    </TeamsGate>
  );
}
